import { Link } from "react-router-dom";
import { colors, fonts, style as semanticStyle } from "../../style";
import { Icon } from "./Icon";
import { Spinner } from "./display";

const buttonVariants = {
  secondary: {
    background: colors.raised,
    color: colors.text,
    border: `1px solid ${colors.borderStrong}`,
  },
  primary: {
    background: colors.teal,
    color: colors.onPrimary,
    border: "1px solid transparent",
  },
  danger: {
    background: colors.red,
    color: colors.text,
    border: "1px solid transparent",
  },
  ghost: {
    background: "none",
    color: colors.textMuted,
    border: "1px solid transparent",
  },
  // AI actions (summarise, ask)
  accent: {
    background: semanticStyle.accentButton.background,
    color: colors.text,
    border: semanticStyle.accentButton.border,
  },
  // Sidekick gradient, for the main AI action
  gradient: semanticStyle.gradientButton,
};

// [with text, icon only]
const buttonPadding = {
  sm: ["5px 10px", "5px 7px"],
  md: ["7px 12px", "7px 9px"],
  // Matches the height of form inputs beside it
  lg: ["9px 16px", "9px 10px"],
};

// No children = icon-only: pass aria-label (and title) too
export function Btn({
  variant = "secondary",
  size = "md",
  icon,
  loading,
  disabled,
  children,
  style,
  as: As = "button",
  ...props
}) {
  return (
    <As
      type={As === "button" ? "button" : undefined}
      className={variant === "gradient" ? "sk-btn sk-gradient" : "sk-btn"}
      disabled={As === "button" ? disabled || loading : undefined}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "6px",
        padding: buttonPadding[size][children ? 0 : 1],
        borderRadius: "9px",
        font: "inherit",
        fontFamily: fonts.system,
        fontSize: "13px",
        fontWeight: 600,
        whiteSpace: "nowrap",
        textDecoration: "none",
        cursor: disabled || loading ? "default" : "pointer",
        opacity: disabled ? 0.5 : 1,
        ...buttonVariants[variant],
        ...style,
      }}
      {...props}
    >
      {loading ? <Spinner size={13} /> : icon && <Icon name={icon} size={14} />}
      {children}
    </As>
  );
}

export function LinkButton({ children, style, ...props }) {
  return (
    <button
      type="button"
      className="sk-link"
      style={{
        border: 0,
        background: "none",
        padding: 0,
        color: colors.teal,
        font: "inherit",
        fontSize: "13px",
        fontWeight: 600,
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        gap: "5px",
        ...style,
      }}
      {...props}
    >
      {children}
    </button>
  );
}

// Bare icon, no button chrome (dismiss crosses, inline pencils)
export function IconButton({
  icon,
  label,
  size = 16,
  color = colors.textMuted,
  style,
  ...props
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className="sk-link"
      style={{
        border: 0,
        background: "none",
        padding: "2px",
        color,
        cursor: "pointer",
        display: "flex",
        ...style,
      }}
      {...props}
    >
      <Icon name={icon} size={size} />
    </button>
  );
}

const backStyle = {
  alignSelf: "flex-start",
  fontSize: "13px",
  color: colors.teal,
  textDecoration: "none",
};

// "‹ label" above a page title: a router link with `to`, else a button
export function BackLink({ to, onClick, children, style }) {
  // One text node for strings, so the link reads (and matches) as "‹ label"
  const text =
    typeof children === "string" ? `‹ ${children}` : <>‹ {children}</>;
  if (to) {
    return (
      <Link to={to} className="sk-link" style={{ ...backStyle, ...style }}>
        {text}
      </Link>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className="sk-link"
      style={{
        ...backStyle,
        border: 0,
        background: "none",
        padding: 0,
        font: "inherit",
        fontSize: "13px",
        cursor: "pointer",
        ...style,
      }}
    >
      {text}
    </button>
  );
}

// Outlined pill for suggestions and quick prompts; selected outlines in teal
export function PillButton({
  selected,
  size = "md",
  children,
  style,
  ...props
}) {
  return (
    <button
      type="button"
      className="sk-row"
      style={{
        padding: size === "sm" ? "3px 9px" : "7px 12px",
        borderRadius: "999px",
        border: `1px solid ${selected ? colors.teal : colors.borderStrong}`,
        background: "none",
        color: colors.textStrong,
        fontFamily: "inherit",
        fontSize: size === "sm" ? "12px" : "13px",
        cursor: "pointer",
        ...style,
      }}
      {...props}
    >
      {children}
    </button>
  );
}
